/** Hooks React Query pour /logements. */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createLogement,
  deleteLogement,
  getLogement,
  listLogements,
  updateLogement,
  type LogementCreatePayload,
} from "@/api/logements";

const LOGEMENTS_KEY = ["logements"] as const;
const LOGEMENT_KEY = (id: string) => ["logements", id] as const;

export function useLogements() {
  return useQuery({
    queryKey: LOGEMENTS_KEY,
    queryFn: listLogements,
    staleTime: 30_000,
  });
}

export function useLogement(id: string | undefined) {
  return useQuery({
    queryKey: id ? LOGEMENT_KEY(id) : ["logements", "unknown"],
    queryFn: () => getLogement(id as string),
    enabled: !!id,
    staleTime: 30_000,
  });
}

export function useCreateLogement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: LogementCreatePayload) => createLogement(payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: LOGEMENTS_KEY }),
  });
}

export function useUpdateLogement(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: Partial<LogementCreatePayload>) =>
      updateLogement(id, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: LOGEMENTS_KEY });
      qc.invalidateQueries({ queryKey: LOGEMENT_KEY(id) });
    },
  });
}

export function useDeleteLogement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteLogement(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: LOGEMENTS_KEY }),
  });
}
